/**
 * Phone Buildings (t220u, docs/mobile-design.md "Buildings"): list order, search, the quick-status line and alerts,
 * maps links, the building Issues filters, severity tones and the one-tap status actions. Pure TS (web and native).
 */
import type { BuildingStatus } from '../types';
import type { IncidentSeverity, IncidentStatus } from '../ops/incidents';

type B = { id: string; name: string; code?: string | null; address?: string | null; type_name?: string | null; kind?: string | null; following?: boolean };

/** My primary building first, then my other buildings, then ones I follow, then the rest; A to Z within each. */
export function orderBuildings<T extends B>(list: T[], status: Pick<BuildingStatus, 'id' | 'mine' | 'primary'>[] = []): T[] {
  const by = new Map(status.map(s => [s.id, s]));
  const rank = (b: T) => { const s = by.get(b.id); return s?.primary ? 0 : s?.mine ? 1 : b.following ? 2 : 3; };
  return [...list].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'en-GB'));
}

/** Name, short code, address or type, case and accent insensitive; every word must match. */
export function matchBuilding(b: B, q: string): boolean {
  const norm = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  const words = norm(q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = norm([b.name, b.code, b.address, b.type_name ?? b.kind].filter(Boolean).join(' '));
  return words.every(w => hay.includes(w));
}

/** The star only means something when there is more than one of my buildings (t201u). */
export const showPrimaryStar = (status: Pick<BuildingStatus, 'mine'>[]) => status.filter(s => s.mine).length > 1;

export type BuildingAlertKind = 'critical' | 'breached' | 'missed';
export interface BuildingAlert { kind: BuildingAlertKind; label: string }
/** Things that need attention at a building, worst first. */
export function buildingAlerts(s: Pick<BuildingStatus, 'issues' | 'checklists'> | undefined): BuildingAlert[] {
  if (!s) return [];
  const out: BuildingAlert[] = [];
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
  if (s.issues.critical) out.push({ kind: 'critical', label: n(s.issues.critical, 'critical issue', 'critical issues') });
  if (s.issues.breached) out.push({ kind: 'breached', label: n(s.issues.breached, 'past its SLA', 'past their SLA') });
  if (s.checklists.missed) out.push({ kind: 'missed', label: n(s.checklists.missed, 'missed checklist', 'missed checklists') });
  return out;
}

/** "3/5" for today's checklists, or null when there are none today. */
export const checklistFraction = (c: BuildingStatus['checklists'] | undefined) => (c && c.total ? `${c.done}/${c.total}` : null);

/** Apple Maps and Google Maps search links for an address (the phone opens the app). */
export function mapsLinks(address: string) {
  const q = encodeURIComponent(address.replace(/\s*\n\s*/g, ', ').trim());
  return { apple: `https://maps.apple.com/?q=${q}`, google: `https://www.google.com/maps/search/?api=1&query=${q}` };
}
/** Apple Maps on iPhone / iPad / Mac, Google Maps everywhere else. */
export const preferredMaps = (ua: string): 'apple' | 'google' => (/iPhone|iPad|iPod|Macintosh/.test(ua) ? 'apple' : 'google');

// ------------------------------------------------------------------------------------------------ building issues
export type BuildingIssueFilter = 'open' | 'mine' | 'resolved';
export const BUILDING_ISSUE_FILTERS: { key: BuildingIssueFilter; label: string }[] = [{ key: 'open', label: 'Open' }, { key: 'mine', label: 'Mine' }, { key: 'resolved', label: 'Resolved' }];
export const parseIssueFilter = (v: string | null | undefined): BuildingIssueFilter => (v === 'mine' || v === 'resolved' ? v : 'open');
/** The incidents list query for a filter at a building ("Mine" = open ones I reported, am assigned or follow). */
export function buildingIssueQuery(locationId: string, f: BuildingIssueFilter): { location_id: string; scope: 'mine' | 'all'; status: 'open' | 'closed'; limit: number } {
  return { location_id: locationId, scope: f === 'mine' ? 'mine' : 'all', status: f === 'resolved' ? 'closed' : 'open', limit: 200 };
}

/** t212u severity colours: low grey, medium amber, high orange, critical red (under the type icon). */
export const SEVERITY_TONE: Record<IncidentSeverity, 'grey' | 'amber' | 'orange' | 'red'> = { low: 'grey', medium: 'amber', high: 'orange', critical: 'red' };

export interface IssueAction { key: string; label: string; to: IncidentStatus; primary?: boolean }
/**
 * One-tap status buttons for someone who can manage the issue (ops.incident.manage there); others get none.
 * New: Acknowledge, Start, Resolve. Acknowledged / On hold: Start, Resolve. In progress: Resolve, On hold. Resolved / closed: Reopen.
 */
export function issueActions(status: IncidentStatus, canManage: boolean): IssueAction[] {
  if (!canManage) return [];
  const start: IssueAction = { key: 'start', label: status === 'on_hold' ? 'Resume' : 'Start', to: 'in_progress' };
  const resolve: IssueAction = { key: 'resolve', label: 'Resolve', to: 'resolved' };
  switch (status) {
    case 'new': return [{ key: 'ack', label: 'Acknowledge', to: 'acknowledged', primary: true }, start, resolve];
    case 'acknowledged': return [{ ...start, primary: true }, resolve];
    case 'on_hold': return [{ ...start, primary: true }, resolve];
    case 'in_progress': return [{ ...resolve, primary: true }, { key: 'hold', label: 'On hold', to: 'on_hold' }];
    default: return [{ key: 'reopen', label: 'Reopen', to: 'in_progress', primary: true }];
  }
}
